import { useState, useEffect } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { CoverageReport } from '@models/index'
import './Gate2Renderer.css'

interface Gate2RendererProps {
  data: CoverageReport | any
  onApprove: (selectedFactors: string[]) => void
}

export function Gate2Renderer({ data, onApprove }: Gate2RendererProps) {
  // Debug log to see actual backend response structure (limited to avoid huge console dumps)
  useEffect(() => {
    console.log('[Gate 2] Data received:', !!data)
    console.log('[Gate 2] Data keys:', data ? Object.keys(data) : 'null/undefined')
    if (data?.proposed_factors) {
      console.log('[Gate 2] Proposed factors count:', data.proposed_factors.length)
      if (data.proposed_factors.length > 0) {
        console.log('[Gate 2] First factor:', data.proposed_factors[0])
      }
    }
    // Log if BSRE analysis is present (but don't dump it)
    if (data?.bsre_analysis) {
      console.log('[Gate 2] BSRE analysis present, keys:', Object.keys(data.bsre_analysis))
    }
  }, [data])

  // Safely extract coverage data with fallbacks
  const coverageDistribution = data?.coverage_distribution || {
    taxonomies: {},
    bsc_perspectives: {}
  }

  const proposedFactors = data?.proposed_factors || []
  const coverageAttestation = data?.coverage_attestation || data?.coverage_statement || 'Coverage analysis complete.'
  const confidenceScore = data?.confidence_score || 0

  const [selectedFactors, setSelectedFactors] = useState<Set<string>>(
    new Set(proposedFactors.map((f: any) => f.factor_id || f.id || f.node_id))
  )
  const [approved, setApproved] = useState(false)

  const handleToggle = (factorId: string) => {
    const newSelection = new Set(selectedFactors)
    if (newSelection.has(factorId)) {
      newSelection.delete(factorId)
    } else {
      newSelection.add(factorId)
    }
    setSelectedFactors(newSelection)
  }

  const handleSelectAll = () => {
    setSelectedFactors(new Set(proposedFactors.map((f: any) => f.factor_id || f.id || f.node_id)))
  }

  const handleDeselectAll = () => {
    setSelectedFactors(new Set())
  }

  const handleApprove = () => {
    setApproved(true)
    onApprove(Array.from(selectedFactors))
  }

  // If no data, show loading/error state
  if (!data || proposedFactors.length === 0) {
    return (
      <div className="gate2-container">
        <div className="error-message">
          <h4>⚠️ Coverage Report Not Available</h4>
          <p>No strategic factors received from the backend. This could indicate:</p>
          <ul>
            <li>The coverage analysis is still processing</li>
            <li>No nodes met the relevance threshold (score &gt;= 7)</li>
            <li>A backend connection issue</li>
          </ul>
          <details>
            <summary>Debug Info (click to expand)</summary>
            <p><strong>Data received:</strong> {data ? 'Yes' : 'No'}</p>
            <p><strong>Proposed factors:</strong> {proposedFactors?.length || 0}</p>
            <p><strong>Coverage distribution:</strong> {JSON.stringify(coverageDistribution, null, 2)}</p>
            <p><strong>Confidence score:</strong> {confidenceScore}%</p>
            <p><strong>Data keys:</strong> {data ? Object.keys(data).join(', ') : 'N/A'}</p>
            <hr />
            <pre style={{ maxHeight: '300px', overflow: 'auto' }}>
              {/* Only show essential fields to avoid huge JSON dumps */}
              {JSON.stringify({
                coverage_distribution: data?.coverage_distribution,
                proposed_factors_count: data?.proposed_factors?.length || 0,
                confidence_score: data?.confidence_score,
                has_bsre_analysis: !!data?.bsre_analysis,
              }, null, 2)}
            </pre>
          </details>
        </div>
      </div>
    )
  }

  return (
    <div className="gate2-container">
      {/* Coverage Distribution Summary */}
      <section className="coverage-section">
        <h4>📊 Coverage Distribution</h4>
        <div className="distribution-summary">
          <div className="summary-item">
            <strong>Taxonomies:</strong>
            <span>{Object.keys(coverageDistribution.taxonomies || {}).length} categories</span>
          </div>
          <div className="summary-item">
            <strong>BSC Perspectives:</strong>
            <span>{Object.keys(coverageDistribution.bsc_perspectives || {}).length} perspectives</span>
          </div>
          <div className="summary-item">
            <strong>Total Factors:</strong>
            <span>{proposedFactors.length} identified</span>
          </div>
        </div>
      </section>

      {/* Proposed Factors Table */}
      <section className="factors-section">
        <div className="section-header">
          <h4>📋 Proposed Strategic Factors</h4>
          <div className="selection-controls">
            <button onClick={handleSelectAll} className="select-btn">Select All</button>
            <button onClick={handleDeselectAll} className="select-btn">Deselect All</button>
            <span className="selection-count">
              {selectedFactors.size} / {proposedFactors.length} selected
            </span>
          </div>
        </div>

        <table className="factors-table">
          <thead>
            <tr>
              <th className="checkbox-col"></th>
              <th>Factor Name</th>
              <th>Rationale</th>
              <th className="relevance-col">Relevance</th>
            </tr>
          </thead>
          <tbody>
            {proposedFactors.map((factor: any, idx: number) => {
              const factorId = factor.factor_id || factor.id || factor.node_id || `factor_${idx}`
              const factorName = factor.factor_name || factor.name || 'Unnamed Factor'
              const reasoning = factor.reasoning || 'No rationale provided'
              // Backend returns relevance_score on 0-10 scale, normalize to 0-1
              const relevanceScore = factor.relevance_score !== undefined
                ? factor.relevance_score / 10
                : (factor.score !== undefined ? factor.score : 0.5)

              return (
                <tr
                  key={factorId}
                  className={selectedFactors.has(factorId) ? 'selected' : ''}
                >
                  <td className="checkbox-col">
                    <input
                      type="checkbox"
                      checked={selectedFactors.has(factorId)}
                      onChange={() => handleToggle(factorId)}
                    />
                  </td>
                  <td className="factor-name">{factorName}</td>
                  <td className="rationale-col">{reasoning}</td>
                  <td className="relevance-col">
                    <div className="relevance-bar">
                      <div
                        className="relevance-fill"
                        style={{ width: `${relevanceScore * 100}%` }}
                      />
                      <span>{(relevanceScore * 100).toFixed(0)}%</span>
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </section>

      {/* Coverage Attestation */}
      {coverageAttestation && (
        <section className="attestation-section">
          <h4>📄 Coverage Attestation</h4>
          <div className="attestation-text">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {coverageAttestation}
            </ReactMarkdown>
          </div>
        </section>
      )}

      {/* Actions */}
      <div className="gate-actions">
        <button
          className={`approve-btn primary ${approved ? 'approved' : ''}`}
          onClick={handleApprove}
          disabled={selectedFactors.size === 0}
        >
          Approve Selection & Continue to Gate 3
        </button>
      </div>
    </div>
  )
}
